import { all, call, put, fork, takeEvery, select } from 'redux-saga/effects';
import {
    fetchProjectsSuccess,
    showProjectMessage,
    onSaveProjectSuccess,
    onDeleteProjectSuccess,
    onImportProjectsSuccess,
    hideProjectLoader,
    hideImportProjectsLoader,
    addFavouriteProjectSuccess,
    onSaveProjectCacheSuccess,
} from 'actions/project';
import {
    ON_SAVE_PROJECT,
    FETCH_ALL_PROJECT,
    ON_DELETE_PROJECT,
    ON_IMPORT_PROJECTS,
    ADD_FAVOURITE_PROJECT,
} from 'constants/ActionTypes';
import { fetchDataRequest, addOrUpdateDataRequest, updateDataRequest } from 'services/ApiService';
import { formatDateDefault } from 'services/DateFormatters';
import { apiErrorMessageFormatter } from 'services/errorService';
import { projectCacheExpired, sanitizeProjectCache } from 'services/projectService';
import {
    ORGID_PATH_PARAM,
    PROJECTS_FETCH_ENDPOINT,
    PROJECT_ADD_ENDPOINT,
    PROJECT_UPDATE_ENDPOINT,
    PROJECTS_ADD_ENDPOINT,
    JIRA_GET_PROJECTS_ENDPOINT
} from 'constants/api';
import { generateGuid, } from 'services/Authentication';

const currentOrg = (state) => state.orgs.selectedOrg;
const importedProjects = (state) => state.projects.importedProjects;
const getProjectList = (state) => state.projects.allProject;
const getProjectCacheList = (state) => state.projects.projectCacheList || [];

// export function* fetchProjectsDeprecated({ payload }) {
//     try {
//         // payload should be the orgid
//         const fetchedProjects = yield call(fetchProjectsRequest, payload) || [];
//         yield put(fetchProjectsSuccess(fetchedProjects));
//     } catch (error) {
//         yield put(showProjectMessage(error.messages));
//     }
// }

const fetchProjectsRequest = async (orgid) => {
    const projects = await fetchDataRequest(PROJECTS_FETCH_ENDPOINT, [ORGID_PATH_PARAM], [orgid]);
    return projects;
};

const fetchImportProjectsRequest = async (orgid) => {
    try {
        const fetchedProjects = await fetchDataRequest(JIRA_GET_PROJECTS_ENDPOINT, [ORGID_PATH_PARAM], [orgid]) || [];
        return fetchedProjects;
    } catch (projectError) {
        // continue processing as most likely the jira call could fail - can still show the local project data
        console.error('An error occurred importing projects - '.concat(projectError.messages));
    }
    return [];
};

export function* fetchProjects({ payload }) {
    // we pass in the orgId as this may be called from the dashboard and a race condition may occur where the selected org isnt set upon making this call !!
    const { orgid, useCache } = payload;
    try {
        const allProjectCache = yield select(getProjectCacheList);
        // clean up the cache to remove stale items - the cache is projects per orgId
        let sanitizedCache = yield call(sanitizeProjectCache, allProjectCache);
        const cacheEntry = sanitizedCache.find(entry => entry?.id === orgid);
        const now = new Date();
        let fetchedProjects = {};
        let replaceCachedEntry = false;
        let addCachedEntry = false;
        let refreshLocalProjectCacheDataOnly = false; // only update local project data in the cache - dont update jira data
        if (cacheEntry) {
            // check the refresh time on the entry to determine which data to use
            if (useCache) {
                if (yield call(projectCacheExpired, cacheEntry?.lastUpdated)) {
                    // entry has expired so retrieve and set
                    replaceCachedEntry = true;
                } else {
                    refreshLocalProjectCacheDataOnly = true;
                    // hasnt expired so only replace the local project data - do not make jira project rest api call
                }
            } else {
                // have cache entry but force refresh so retrieve and set
                replaceCachedEntry = true;
            }
        } else {
            // no cache entry so fetch and add to cache
            addCachedEntry = true;
        }
        if (refreshLocalProjectCacheDataOnly) {
            // just replace the cache with the local data
            fetchedProjects = yield call(fetchProjectsRequest, orgid);
        } else {
            // server will do all the work of keeping the jira projects and the local projects in sync
            fetchedProjects = yield call(fetchImportProjectsRequest, orgid);
        }
        if (replaceCachedEntry || addCachedEntry) {
            // only 1 entry in the cahce so just replace it
            sanitizedCache = new Array({ id: orgid, lastUpdated: now, data: fetchedProjects });
            yield put(onSaveProjectCacheSuccess(sanitizedCache));
        }
        yield put(fetchProjectsSuccess(fetchedProjects));
    } catch (error) {
        yield put(showProjectMessage(error.messages));
    }
}

/**
 * Deprecated
 * Not being used - works with improtProjectsModal that calls fetchImportProjects - waits for user confirmation then this is called
 */
export function* onImportProjects() {
    const allProjects = yield select(getProjectList);
    const allImportedProjects = yield select(importedProjects);
    const selectedOrg = yield select(currentOrg);
    try {
        const newProjects = yield call(onImportProjectsRequest, allImportedProjects, selectedOrg) || [];
        if (newProjects?.length > 0) {
            // change the payload to include the orgid for security reasons - checks against the path param
            const newPayload = {
                orgid: selectedOrg?.id,
                projects: newProjects
            };
            // only send new projects
            yield call(
                updateDataRequest,
                newPayload,
                PROJECTS_ADD_ENDPOINT,
                'Adding '.concat(newPayload?.projects?.length).concat(' projects'),
                [ORGID_PATH_PARAM],
                [selectedOrg?.id],
            );
            // TODO - change the response into messages for the client
            // TODO - put message in normal place or another spot to display on a larger alert
            // response is {messages: [{successMsg: ''}, {errorMsg: ''}], errorCount: 0, successCount: 0}
            // yield put(showProjectMessage(error.messages));
            const combinedProjects = allProjects.concat(newProjects);
            // put all projects back into the store
            yield put(onImportProjectsSuccess(combinedProjects));
        } else {
            // dont touch the store as no action was taken
            // display message and stop any loaders
            yield put(showProjectMessage('No projects were imported!'));
            yield put(hideProjectLoader());
            yield put(hideImportProjectsLoader());
        }
    } catch (error) {
        yield put(showProjectMessage(error.messages));
    }
}

const onImportProjectsRequest = async (importProjects, selectedOrg) => {
    try {
        const today = new Date();
        if (Array.isArray(importProjects) && importProjects.length > 0) {
            // create transformed objects to use for the update
            const convertedProjectsToImport = importProjects.map((project) => {
                return {
                    id: generateGuid(),
                    orgid: selectedOrg?.id,
                    name: project?.name,
                    description: 'JIRA project '.concat(project?.name).concat(' imported on ').concat(formatDateDefault(today)),
                    startDate: today,
                    endDate: today,
                    weight: 0,
                    busValPriorityParams: [],
                    costImplPriorityParams: [],
                    jiraProjectAttributes: { ...project },
                    budget: 0,
                    selected: false,
                    starred: false,
                    frequently: false,
                    thumb: null,
                };
            });
            return convertedProjectsToImport;
        }
        return [];
    } catch (error) {
        throw error;
    }
};

/**
 * Deprecated
 * Not being used - works with improtProjectsModal that calls this method - waits for user confirmation then this is called
 */
// export function* fetchImportProjectsDeprecated({ payload }) {
//     try {
//         // const allSecuritys = yield select(getSecurityList);
//         // let securityParams = {};
//         // for (const security of allSecuritys) {
//         //     if (security?.id === payload) {
//         //         securityParams = security;
//         //         break;
//         //     }
//         // }
//         const selectedOrg = yield select(currentOrg);
//         const fetchedProjects = yield call(fetchImportProjectsRequest, selectedOrg, payload) || [];
//         yield put(fetchImportProjectsSuccess(fetchedProjects));
//     } catch (error) {
//         yield put(showProjectMessage(error.messages));
//     }
// }

// const fetchImportProjectsRequest = async (securityId) => {

// need a call to the JIRA apis for the rest call
// 1. create api token in JIRA - https://confluence.atlassian.com/cloud/api-tokens-938839638.html
// 2. get projects - https://developer.atlassian.com/cloud/jira/platform/rest/v3/?utm_source=%2Fcloud%2Fjira%2Fplatform%2Frest%2F&utm_medium=302#api-rest-api-3-project-get
// TODO fetch via the JIRA API
//     const importProjectList = [
//         {
//             'self': 'http://your-domain.atlassian.net/rest/api/3/project/EX',
//             'id': '10058',
//             'key': 'EX',
//             'name': 'NEW-Example',
//             'avatarUrls': {
//                 '48x48': 'http://your-domain.atlassian.net/secure/projectavatar?size=large&pid=10000',
//                 '24x24': 'http://your-domain.atlassian.net/secure/projectavatar?size=small&pid=10000',
//                 '16x16': 'http://your-domain.atlassian.net/secure/projectavatar?size=xsmall&pid=10000',
//                 '32x32': 'http://your-domain.atlassian.net/secure/projectavatar?size=medium&pid=10000'
//             },
//             'projectCategory': {
//                 'self': 'http://your-domain.atlassian.net/rest/api/3/projectCategory/10000',
//                 'id': '10000',
//                 'name': 'FIRST',
//                 'description': 'First Project Category'
//             },
//             'simplified': false,
//             'style': 'classic'
//         },
//         {
//             'self': 'http://your-domain.atlassian.net/rest/api/3/project/ABC',
//             'id': '10007',
//             'key': 'KABC',
//             'name': 'KAlphabetical',
//             'avatarUrls': {
//                 '48x48': 'http://your-domain.atlassian.net/secure/projectavatar?size=large&pid=10001',
//                 '24x24': 'http://your-domain.atlassian.net/secure/projectavatar?size=small&pid=10001',
//                 '16x16': 'http://your-domain.atlassian.net/secure/projectavatar?size=xsmall&pid=10001',
//                 '32x32': 'http://your-domain.atlassian.net/secure/projectavatar?size=medium&pid=10001'
//             },
//             'projectCategory': {
//                 'self': 'http://your-domain.atlassian.net/rest/api/3/projectCategory/10000',
//                 'id': '10000',
//                 'name': 'FIRST',
//                 'description': 'First Project Category'
//             },
//             'simplified': false,
//             'style': 'netx-gen'
//         }
//     ];
//     return importProjectList;
// };

/**
 * Not in effect at the moment. Set the end date to finalise the project life
 * @param {*} param0 
 */
export function* deleteProject({ payload }) {
    try {
        yield call(deleteProjectRequest, payload?.id);
        yield put(onDeleteProjectSuccess(payload?.id));
    } catch (error) {
        yield put(showProjectMessage(error.messages));
    }
}

const deleteProjectRequest = async () => {
    // TODO - deleteDataRequest
    // // AUTO-GENERATED-TODO the uri for this request is /organisation/{orgid}/project(s). You will need to add path params - refer to replaceEndpointPathParams in ApiConfig for further details
    // return deleteDataRequest('deleteProject', id);
};

export function* saveProject({ payload }) {
    try {
        const allProjects = yield select(getProjectList);
        const selectedOrg = yield select(currentOrg);
        const savedProject = yield call(
            addOrUpdateDataRequest,
            allProjects,
            payload,
            PROJECT_ADD_ENDPOINT,
            PROJECT_UPDATE_ENDPOINT,
            [ORGID_PATH_PARAM],
            [selectedOrg.id]
        );
        yield put(onSaveProjectSuccess(savedProject));
    } catch (error) {
        yield put(showProjectMessage(apiErrorMessageFormatter(error)));
    }
}

/**
 * This is when the user selects the star on the list page - update the starred attribute of the entity
 * @param {*} param0 
 */
export function* addFavouriteProject({ payload }) {
    try {
        const allProjects = yield select(getProjectList);
        const selectedOrg = yield select(currentOrg);
        const filtered = allProjects?.filter((entry) => entry?.id === payload?.id);

        if (filtered?.length > 0) {
            const entry = filtered[0];
            const updated = {
                ...entry,
                starred: !payload.starred
            };
            const auditMsg = 'Updating starred setting to '.concat(updated?.starred).concat(' for project id ').concat(updated?.id);
            yield call(
                updateDataRequest,
                updated,
                PROJECT_UPDATE_ENDPOINT,
                auditMsg,
                [ORGID_PATH_PARAM],
                [selectedOrg?.id],
            );
            // now just pass on the original payload as the reducer will filter the list as per the state of the selection
            yield put(addFavouriteProjectSuccess(payload));
        }
    } catch (error) {
        yield put(showProjectMessage(error.messages));
    }
}

export function* fetchProjectsSaga() {
    yield takeEvery(FETCH_ALL_PROJECT, fetchProjects);
}

export function* saveProjectSaga() {
    yield takeEvery(ON_SAVE_PROJECT, saveProject);
}

export function* deleteProjectSaga() {
    yield takeEvery(ON_DELETE_PROJECT, deleteProject);
}

// export function* fetchImportProjectsSaga() {
//     yield takeEvery(FETCH_IMPORT_PROJECTS, fetchImportProjects);
// }

export function* onImportProjectsSaga() {
    yield takeEvery(ON_IMPORT_PROJECTS, onImportProjects);
}

export function* addFavouriteProjectSaga() {
    yield takeEvery(ADD_FAVOURITE_PROJECT, addFavouriteProject);
}

export default function* rootSaga() {
    yield all([
        fork(fetchProjectsSaga),
        fork(addFavouriteProjectSaga),
        fork(saveProjectSaga),
        fork(deleteProjectSaga),
        // fork(fetchImportProjectsSaga),
        fork(onImportProjectsSaga),
    ]);
}


// WEBPACK FOOTER //
// ./sagas/project.js